"use client";

import { useEffect, useRef } from "react";
import Image from "next/image";
import * as m from "motion/react-m";
import { AnimatePresence, type MotionValue } from "motion/react";
import clsx from "clsx";
import { useTranslations } from "next-intl";

import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import IconFeature from "@ui/central-icons/icons/IconFeature";
import { smoothstep } from "@ui/lib/math";

import { ElevatedButton } from "./ElevatedButton";
import { type InteractiveImageCreationResult, type InteractiveImageGenerationError, makeCrossfade } from "./types";

const FULL_INSET = { top: "0%", left: "0%", right: "0%", bottom: "0%" } as const;
const CARD_CLASS = "tw-absolute tw-overflow-hidden tw-rounded-2.5xl";
const PLACEHOLDER_CARD_CLASS = clsx(CARD_CLASS, "tw-bg-gray-200");
const DOT_GRID_SPACING = 16;
const DOT_GRID_MOBILE_SPACING = 14;
const DOT_GRID_MOBILE_QUERY = "(max-width: 767px)";
const DOT_GRID_RADIUS = 0.9;
const DOT_GRID_BASE_OPACITY = 0.06;
const DOT_GRID_WAVE_OPACITY = 0.2;
const DOT_GRID_WAVE_RADIUS = 320;
const DOT_GRID_CYCLE_SPEED = 0.00016;
const DOT_GRID_PHASE_WARP = 0.14;
const DOT_GRID_START_JITTER = 0.055;
const DOT_GRID_VISIBLE_PADDING = 0.04;

export type CreateState = "idle" | "generating" | "result";

interface CreateModeProps {
  createState: CreateState;
  /** Shared card scale: empty/generating stay inset, a loaded result grows full-frame. */
  cardScale: MotionValue<number>;
  /** Inverse-ish companions that keep placeholder copy readable and image crops lively. */
  contentScale: MotionValue<number>;
  imageScale: MotionValue<number>;
  /** A result has been revealed at least once (drives full-bleed vs inset card). */
  hasResult: boolean;
  /** The result to display; its identity (via resultKey) drives the crossfade. */
  result?: InteractiveImageCreationResult;
  resultKey: number;
  resultLoaded: boolean;
  reduceMotion: boolean;
  /** Link for the gated-error sign-up CTA; falls back to a button. */
  signUpHref?: string;
  /** A generation failure to surface in the inset card (live mode only). */
  generationError?: InteractiveImageGenerationError;
  /** Fires once a result image has loaded, with the key that loaded so a late load from a
   *  superseded result can't mark a newer one ready. */
  onResultLoad: (loadedKey: number) => void;
  /** Retry the last generation (the "Try again" affordance on a non-gated error). */
  onRetry?: () => void;
}

export function CreateMode({
  createState,
  cardScale,
  contentScale,
  imageScale,
  hasResult,
  result,
  resultKey,
  resultLoaded,
  reduceMotion,
  signUpHref,
  generationError,
  onResultLoad,
  onRetry,
}: CreateModeProps) {
  const t = useTranslations("ClientKeys.InteractiveImage");

  const isGenerating = createState === "generating";
  const isResultLoading = createState === "result" && hasResult && !resultLoaded && !generationError;
  // Placeholder shows the empty hint before generation, then keeps the dotted loading surface
  // until the result image has actually loaded.
  const showPlaceholder = createState !== "result" || isResultLoading;
  const showGenerating = isGenerating || isResultLoading;

  const crossfade = makeCrossfade(reduceMotion);
  const cardStyle = { ...FULL_INSET, scale: cardScale };

  // Announced to screen readers: most relevant of error / generating / ready.
  const liveStatus = generationError
    ? generationError.message
    : showGenerating
      ? t("generating")
      : createState === "result"
        ? t("result_ready")
        : "";

  return (
    <>
      {/* Polite live region kept mounted across every state, since a region inserted at the moment
          of change is often skipped by screen readers. */}
      <div role='status' aria-live='polite' aria-atomic='true' className='tw-sr-only'>
        {liveStatus}
      </div>

      <AnimatePresence>
        {showPlaceholder && (
          <m.div
            key='placeholder'
            className={PLACEHOLDER_CARD_CLASS}
            style={cardStyle}
            initial={{ opacity: hasResult ? 0 : 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={crossfade}
          >
            <AnimatePresence mode='wait'>
              {showGenerating ? (
                <m.div
                  key='generating'
                  className='tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={crossfade}
                >
                  <AnimatedDotGrid scale={imageScale} reduceMotion={reduceMotion} />
                  <div className='tw-absolute tw-left-1/2 tw-top-1/2 tw-h-24 tw-w-96 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-bg-gray-200 [-webkit-mask-image:radial-gradient(ellipse_at_center,black_0%,black_24%,rgb(0_0_0/0.55)_58%,transparent_94%)] [mask-image:radial-gradient(ellipse_at_center,black_0%,black_24%,rgb(0_0_0/0.55)_58%,transparent_94%)]' />
                  <m.p
                    className='shimmer-text tw-relative tw-inline-block tw-py-1 tw-leading-normal tw-type-sm'
                    style={{ scale: contentScale }}
                  >
                    {t("generating")}
                  </m.p>
                </m.div>
              ) : (
                <m.div
                  key='empty'
                  className='tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-px-6 tw-text-center'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={crossfade}
                >
                  <m.div
                    className='tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4'
                    style={{ scale: contentScale }}
                  >
                    <span className='tw-flex tw-size-10 tw-items-center tw-justify-center tw-rounded-[0.625rem] tw-border-0.5 tw-border-black/10'>
                      <IconFeature className={clsx(iconStrokeFix, "tw-size-6 tw-text-black/60 [&_path]:tw-stroke-2")} />
                    </span>
                    <p className='tw-max-w-80 tw-text-balance tw-text-black/60 tw-type-sm'>{t("empty_hint")}</p>
                  </m.div>
                </m.div>
              )}
            </AnimatePresence>
          </m.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {hasResult && result && (
          <m.div
            key='result'
            // The card scale is target-aimed by the shared create-layer logic: results grow
            // full-frame and generation returns to the inset card.
            className={CARD_CLASS}
            style={cardStyle}
            initial={{ opacity: 0 }}
            animate={{ opacity: isGenerating || !resultLoaded ? 0 : 1 }}
            exit={{ opacity: 0 }}
            transition={crossfade}
          >
            <AnimatePresence initial={false}>
              <m.div
                key={resultKey}
                className='tw-absolute tw-inset-0'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{ scale: imageScale }}
                transition={crossfade}
              >
                <Image
                  src={result.imageUrl}
                  alt={result.alt ?? ""}
                  fill
                  draggable={false}
                  className='tw-select-none tw-object-cover'
                  sizes='(max-width: 64rem) 100vw, 73.5rem'
                  placeholder={result.blurDataURL ? "blur" : "empty"}
                  blurDataURL={result.blurDataURL}
                  onLoad={() => onResultLoad(resultKey)}
                />
              </m.div>
            </AnimatePresence>
          </m.div>
        )}
      </AnimatePresence>

      {/* Generation failure (live mode): card at the inset rect covering whatever is beneath. A
          "gated" error (quota / rate-limit) shows the sign-up CTA; any other shows "Try again". */}
      <AnimatePresence>
        {generationError && (
          <m.div
            key='error'
            className={clsx(
              PLACEHOLDER_CARD_CLASS,
              "tw-z-10 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-px-6 tw-text-center",
            )}
            style={cardStyle}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={crossfade}
          >
            <m.div className='tw-flex tw-flex-col tw-items-center tw-gap-4' style={{ scale: contentScale }}>
              <p className='tw-max-w-72 tw-text-balance tw-text-black/60 tw-type-sm'>{generationError.message}</p>
              {generationError.kind === "gated" ? (
                <ElevatedButton href={generationError.ctaUrl ?? signUpHref}>{t("sign_up")}</ElevatedButton>
              ) : (
                <ElevatedButton onClick={onRetry}>{t("error_retry")}</ElevatedButton>
              )}
            </m.div>
          </m.div>
        )}
      </AnimatePresence>
    </>
  );
}

function AnimatedDotGrid({ scale, reduceMotion }: { scale: MotionValue<number>; reduceMotion: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;

    const draw = (time = 0) => {
      const width = canvas.offsetWidth;
      const height = canvas.offsetHeight;
      const pixelRatio = window.devicePixelRatio || 1;
      const nextWidth = Math.max(1, Math.round(width * pixelRatio));
      const nextHeight = Math.max(1, Math.round(height * pixelRatio));

      if (canvas.width !== nextWidth || canvas.height !== nextHeight) {
        canvas.width = nextWidth;
        canvas.height = nextHeight;
        context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      }

      const point = reduceMotion ? null : getDotGridPoint(time, width, height, scale.get());

      context.clearRect(0, 0, width, height);
      context.fillStyle = "black";

      const spacing = window.matchMedia(DOT_GRID_MOBILE_QUERY).matches ? DOT_GRID_MOBILE_SPACING : DOT_GRID_SPACING;
      const startX = getCenteredDotGridStart(width, spacing);
      const startY = getCenteredDotGridStart(height, spacing);

      for (let y = startY; y < height; y += spacing) {
        for (let x = startX; x < width; x += spacing) {
          const distance = point ? Math.hypot(x - point.x, y - point.y) : DOT_GRID_WAVE_RADIUS;
          const strength = smoothstep(Math.max(0, 1 - distance / DOT_GRID_WAVE_RADIUS));

          context.globalAlpha = DOT_GRID_BASE_OPACITY + strength * DOT_GRID_WAVE_OPACITY;
          context.beginPath();
          context.arc(x, y, DOT_GRID_RADIUS, 0, Math.PI * 2);
          context.fill();
        }
      }

      context.globalAlpha = 1;
    };

    const animate = (time: number) => {
      draw(time);
      frameRef.current = reduceMotion ? null : window.requestAnimationFrame(animate);
    };

    const resizeObserver = new ResizeObserver(() => draw());
    resizeObserver.observe(canvas);
    draw();

    if (!reduceMotion) {
      frameRef.current = window.requestAnimationFrame(animate);
    }

    return () => {
      resizeObserver.disconnect();
      if (frameRef.current !== null) window.cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    };
  }, [reduceMotion]);

  return (
    <m.div className='tw-pointer-events-none tw-absolute tw-inset-0' style={{ scale }}>
      <canvas ref={canvasRef} className='tw-block tw-size-full' aria-hidden />
    </m.div>
  );
}

function getCenteredDotGridStart(size: number, spacing: number) {
  const center = size / 2;
  const offset = center % spacing;

  return offset === 0 ? spacing : offset;
}

function getDotGridPoint(time: number, width: number, height: number, scale: number) {
  const phase = getDotGridPhase(time);
  const visibleInset = Math.max(0, (1 - 1 / Math.max(1, scale)) / 2);
  const visibleRadius = Math.max(0, 0.5 - visibleInset - DOT_GRID_VISIBLE_PADDING);
  const xRadius = Math.min(0.46, visibleRadius);
  const yRadius = Math.min(0.4, visibleRadius);

  return {
    x: width * (0.5 + Math.cos(phase) * xRadius),
    y: height * (0.5 + Math.sin(phase * 0.73) * yRadius),
  };
}

function getDotGridPhase(time: number) {
  const progress = (time * DOT_GRID_CYCLE_SPEED) % 1;
  const startWeight = Math.max(0, 1 - progress / 0.28) ** 2;
  const startJitter =
    (Math.sin(progress * Math.PI * 18) + Math.sin(progress * Math.PI * 47 + 0.8) * 0.5) *
    DOT_GRID_START_JITTER *
    startWeight;
  const warpedProgress = progress + Math.sin(progress * Math.PI * 2) * DOT_GRID_PHASE_WARP + startJitter;

  return warpedProgress * Math.PI * 2;
}
